import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Repeat, Plus } from "lucide-react";
import { formatMoney, RECURRING_FREQUENCY_LABELS } from "@finora/config";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

export function RecurringInvoicesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ["recurring-invoices"],
    queryFn: () => api.listRecurringInvoiceTemplates(),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.updateRecurringInvoiceTemplate(id, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recurring-invoices"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Ponavljajuće fakture</h1>
          <p className="text-sm text-slate-500">
            Šabloni iz kojih se automatski generišu fakture (održavanje, pretplate, ugovori)
          </p>
        </div>
        <Button onClick={() => navigate("/ponavljajuce-fakture/nova")}>
          <Plus className="h-4 w-4" aria-hidden />
          Novi šablon
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !templates || templates.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={Repeat}
              title="Nema ponavljajućih faktura"
              description="Napravite šablon za klijente sa mesečnim/godišnjim uslugama."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Kupac</th>
                <th>Učestalost</th>
                <th>Sledeće izdavanje</th>
                <th>Ukupno po fakturi</th>
                <th>Auto-slanje</th>
                <th>Aktivan</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => {
                const total = t.items.reduce((sum, item) => sum + Number(item.unitPriceGross) * Number(item.quantity), 0);
                return (
                  <tr key={t.id}>
                    <td className="font-medium text-slate-800">{t.customer?.name ?? "—"}</td>
                    <td className="text-slate-500">{RECURRING_FREQUENCY_LABELS[t.frequency]}</td>
                    <td className="text-slate-500">
                      {new Date(t.nextRunDate).toLocaleDateString("sr-RS")}
                    </td>
                    <td>{formatMoney(total.toFixed(2))}</td>
                    <td className="text-slate-500">{t.autoSend ? "Da" : "Ne"}</td>
                    <td>
                      <button
                        onClick={() => toggleActive.mutate({ id: t.id, active: !t.active })}
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          t.active
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {t.active ? "Aktivan" : "Pauziran"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
